/**
 * Theme JavaScript entry point.
 *
 * Exposes the small shared helpers every section script builds on:
 *   Recon.ready(fn)            run fn once the DOM is parsed
 *   Recon.init(selector, fn)   run fn once per matching element, guarded by data-ready
 *   Recon.formatMoney(cents)   format an amount in pence as GBP
 *   Recon.cart                 read from and add to the cart, keeping the header in sync
 *
 * Any cart change dispatches `recon:cart:updated` on `document` with the cart as `detail`.
 */
(() => {
  const ready = (fn) =>
    document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn);

  const init = (selector, fn) =>
    document.querySelectorAll(selector).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      fn(el);
    });

  const formatMoney = (cents) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);

  const cart = {
    async fetch() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    async add(payload) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await response.json();

      const state = await this.fetch();
      this.render(state);
      document.dispatchEvent(new CustomEvent('recon:cart:updated', { detail: state }));
      return state;
    },

    render(state) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = state.item_count;
      });
      document.querySelectorAll('[data-cart-total]').forEach((el) => {
        el.textContent = formatMoney(state.total_price);
      });
    },
  };

  window.Recon = { ready, init, formatMoney, cart };

  /** Publish the sticky header's height so anchors can offset with `scroll-margin-top`. */
  ready(() =>
    init('[data-header]', (el) => {
      const set = () =>
        document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
      set();
      new ResizeObserver(set).observe(el);
    })
  );

  /**
   * Add to basket buttons on product cards and the featured product.
   *
   * Posts the button's variant to the cart, confirms for a moment (a check icon, or
   * "Added ✓" when the button has a `[data-add-label]`), and reports failures through
   * a visually hidden live region rather than an alert.
   */
  const CHECK_ICON = (size) =>
    `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>`;

  ready(() =>
    init('[data-add-to-cart]', (button) => {
      const status = document.createElement('span');
      status.className = 'sr-only';
      status.setAttribute('role', 'status');
      button.insertAdjacentElement('afterend', status);

      /* Snapshot the original icon once: a click during the confirmation window
         must still revert to this, never to the check icon it replaced. */
      const markup = button.innerHTML;
      const icon = button.querySelector('svg');
      const size = icon ? icon.getAttribute('width') : 16;

      let timer;

      button.addEventListener('click', async () => {
        const id = button.dataset.variantId;
        if (!id || button.getAttribute('aria-busy') === 'true') return;

        clearTimeout(timer);
        button.setAttribute('aria-busy', 'true');
        delete button.dataset.error;
        status.textContent = '';

        try {
          await cart.add({ id, quantity: 1 });
          const label = button.querySelector('[data-add-label]');
          if (label) label.textContent = 'Added ✓';
          else button.innerHTML = CHECK_ICON(size);
          status.textContent = 'Added to your basket.';
          timer = setTimeout(() => {
            button.innerHTML = markup;
          }, 1500);
        } catch (error) {
          const message =
            (error && error.description) || 'Sorry, that could not be added to your basket.';
          button.dataset.error = message;
          status.textContent = message;
        } finally {
          button.removeAttribute('aria-busy');
        }
      });
    })
  );

  /**
   * Collection filtering.
   *
   * The filter form and the sort select are plain GET controls that work on their
   * own; these listeners only remove the extra click on the submit button. The
   * sort select is associated with the sidebar form through its `form` attribute,
   * so its change event never reaches that form and cannot submit it twice.
   */
  const submitForm = (form) => (form.requestSubmit ? form.requestSubmit() : form.submit());

  ready(() =>
    init('[data-filter-form]', (form) => {
      form.addEventListener('change', () => submitForm(form));
    })
  );

  ready(() =>
    init('[data-sort]', (select) => {
      select.addEventListener('change', () => {
        if (select.form) submitForm(select.form);
      });
    })
  );

  /** The small screen filter drawer: a modal dialog that closes on its backdrop too. */
  ready(() =>
    init('[data-filter-drawer]', (drawer) => {
      /* Only this section's toggle, so two collection sections on one page keep
         their own drawers. */
      const scope = drawer.closest('.collection') || document;

      scope.querySelectorAll('[data-filter-open]').forEach((button) => {
        button.addEventListener('click', () => drawer.showModal());
      });

      drawer.querySelectorAll('[data-filter-close]').forEach((button) => {
        button.addEventListener('click', () => drawer.close());
      });

      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) drawer.close();
      });

      /* The stylesheet hides the drawer from the desktop layout, but a hidden modal
         still holds the page inert, so close it for real when the sidebar takes over. */
      const desktop = window.matchMedia('(min-width: 64em)');
      desktop.addEventListener('change', (event) => {
        if (event.matches && drawer.open) drawer.close();
      });
    })
  );

  /** Reads a `<script type="application/json">` payload without taking the page down with it. */
  const readJSON = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return fallback;
    }
  };

  /**
   * The product page.
   *
   * One module drives the whole buy box: the media gallery, the variant picker (which
   * repaints the price, SKU and availability), the quantity stepper, and the form's
   * AJAX add to basket. Everything degrades to a plain form post without it.
   */
  ready(() =>
    init('[data-product]', (root) => {
      const variants = readJSON(root.querySelector('[data-variants]'), []);
      const leadTimes = readJSON(root.querySelector('[data-lead-times]'), {});

      /* ---------------- Gallery ---------------- */
      const gallery = root.querySelector('[data-gallery]');
      if (gallery) {
        const images = gallery.querySelectorAll('[data-gallery-image]');
        const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            const index = Number(thumb.dataset.index);
            images.forEach((image, i) => {
              image.hidden = i !== index;
            });
            thumbs.forEach((other, i) => {
              if (i === index) other.setAttribute('aria-current', 'true');
              else other.removeAttribute('aria-current');
            });
          });
        });
      }

      /* ---------------- Quantity ---------------- */
      const qty = root.querySelector('[data-qty]');
      const qtyDec = root.querySelector('[data-qty-dec]');
      const qtyInc = root.querySelector('[data-qty-inc]');

      const setQty = (value) => {
        if (!qty) return;
        const clamped = Math.min(99, Math.max(1, Number.isNaN(value) ? 1 : value));
        qty.value = String(clamped);
        if (qtyDec) qtyDec.disabled = clamped <= 1;
      };

      if (qty) {
        setQty(parseInt(qty.value, 10));
        qty.addEventListener('change', () => setQty(parseInt(qty.value, 10)));
        if (qtyDec) qtyDec.addEventListener('click', () => setQty(parseInt(qty.value, 10) - 1));
        if (qtyInc) qtyInc.addEventListener('click', () => setQty(parseInt(qty.value, 10) + 1));
      }

      /* ---------------- Variant selection ---------------- */
      const priceEl = root.querySelector('[data-price]');
      const skuEl = root.querySelector('[data-sku]');
      const availabilityEl = root.querySelector('[data-availability]');
      const availabilityDot = root.querySelector('[data-availability-dot]');
      const availabilityText = root.querySelector('[data-availability-text]');
      const savingBadge = root.querySelector('[data-gallery-saving]');
      const form = root.querySelector('[data-product-form]');
      const submit = form ? form.querySelector('[type="submit"]') : null;
      const submitLabel = submit ? submit.querySelector('[data-add-label]') : null;
      const addLabel = 'Add to basket';

      /* Mirrors `snippets/price.liquid` at `size: 'xl'` so the rebuilt markup keeps the
         same class names as the server rendered one. `component-price.css` is loaded once
         from the layout, so replacing this markup cannot take its stylesheet with it. */
      const renderPrice = (variant) => {
        if (!priceEl) return;
        const saving =
          variant.compare_at_price && variant.compare_at_price > variant.price
            ? ` <s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
            : '';
        priceEl.innerHTML = `<p class="price price--xl"><span class="price__amount">${formatMoney(
          variant.price
        )}</span>${saving} <span class="price__vat">+VAT</span></p>`;
      };

      const selectVariant = (variant) => {
        if (!variant) return;

        renderPrice(variant);

        if (savingBadge) {
          const saving = (variant.compare_at_price || 0) - variant.price;
          savingBadge.hidden = saving <= 0;
          if (saving > 0) savingBadge.textContent = `Save ${formatMoney(saving)}`;
        }

        if (skuEl) skuEl.textContent = variant.sku || '';

        if (availabilityEl) {
          const lead = leadTimes[variant.id];
          if (availabilityDot) availabilityDot.classList.toggle('product__dot--out', !variant.available);
          if (availabilityText) {
            availabilityText.textContent = lead || (variant.available ? 'In stock' : 'Out of stock');
          }
        }

        if (submit) {
          submit.disabled = !variant.available;
          if (submitLabel) submitLabel.textContent = variant.available ? addLabel : 'Sold out';
        }

        /* Keep the address bar shareable without adding a history entry per click. */
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      root.querySelectorAll('[data-variant-input]').forEach((input) => {
        input.addEventListener('change', () => {
          selectVariant(variants.find((variant) => String(variant.id) === input.value));
        });
      });

      /* ---------------- Add to basket ---------------- */
      if (form) {
        const status = form.querySelector('[data-form-error]');
        let timer;

        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          /* `aria-busy` doubles as the in-flight flag, so a double click posts once. */
          if (!submit || submit.disabled || submit.getAttribute('aria-busy') === 'true') return;

          clearTimeout(timer);
          submit.setAttribute('aria-busy', 'true');
          if (status) status.textContent = '';

          try {
            await cart.add(Object.fromEntries(new FormData(form)));
            if (submitLabel) {
              submitLabel.textContent = 'Added ✓';
              timer = setTimeout(() => {
                submitLabel.textContent = submit.disabled ? 'Sold out' : addLabel;
              }, 1500);
            }
          } catch (error) {
            const message =
              (error && error.description) || 'Sorry, that could not be added to your basket.';
            if (status) status.textContent = message;
          } finally {
            submit.removeAttribute('aria-busy');
          }
        });
      }
    })
  );

  /**
   * Rich text (`.rte`): admin HTML gives bare tables, so wrap each one in a
   * horizontal scroller rather than let a wide table widen the page.
   */
  ready(() =>
    init('.rte table', (table) => {
      if (table.parentElement?.classList.contains('rte__table')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'rte__table';
      wrapper.setAttribute('tabindex', '0');
      table.replaceWith(wrapper);
      wrapper.append(table);
    })
  );
  /**
   * Contact page.
   *
   * Preselects the enquiry type from a `?type=` query parameter so service pages can
   * deep-link to the form, drops any post where the honeypot was filled in, and moves
   * focus to the success or error notice after Shopify re-renders the page.
   */
  ready(() =>
    init('[data-contact-form]', (form) => {
      const select = form.querySelector('[data-enquiry-type]');
      const wanted = new URLSearchParams(window.location.search).get('type');
      if (select && wanted) {
        const key = wanted.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const options = [...select.options].filter((o) => o.dataset.type);
        const match =
          options.find((o) => o.dataset.type === key) ||
          options.find((o) => o.dataset.type.startsWith(key) || key.startsWith(o.dataset.type));
        if (match) select.value = match.value;
      }

      const trap = form.querySelector('[data-honeypot]');
      const submit = form.querySelector('[type="submit"]');

      // The busy state is only ever a hint: Shopify's captcha can swallow the submit before any
      // request fires, so anything that could mean "the challenge went away" clears it again.
      let busyTimer;
      const setBusy = (on) => {
        if (!submit) return;
        clearTimeout(busyTimer);
        submit.classList.toggle('is-busy', on);
        if (on) {
          submit.setAttribute('aria-busy', 'true');
          busyTimer = setTimeout(() => setBusy(false), 8000);
        } else {
          submit.removeAttribute('aria-busy');
        }
      };
      form.addEventListener('submit', (event) => {
        if (trap && trap.value) {
          event.preventDefault();
          return;
        }
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
        setBusy(true);
      });
      window.addEventListener('pageshow', () => setBusy(false));
      window.addEventListener('focus', () => {
        if (submit && submit.classList.contains('is-busy')) setTimeout(() => setBusy(false), 1000);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') setBusy(false);
      });

      const notice = form.querySelector('[data-contact-notice]');
      if (notice) {
        notice.focus({ preventScroll: true });
        notice.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    })
  );

  /**
   * Live open/closed status on the contact page, worked out in the shop's own time zone
   * from the opening hours in the section settings. Stays hidden if anything is missing.
   */
  ready(() =>
    init('[data-open-status]', (el) => {
      const label = el.querySelector('[data-open-status-label]');
      const parse = (t) => {
        const m = /^(\d{1,2}):(\d{2})$/.exec((t || '').trim());
        return m ? Number(m[1]) * 60 + Number(m[2]) : null;
      };
      const open = parse(el.dataset.open);
      const close = parse(el.dataset.close);
      const days = (el.dataset.days || '')
        .split(',')
        .map((d) => Number(d.trim()))
        .filter((d) => d >= 0 && d <= 6);
      if (!label || open === null || close === null || !days.length) return;

      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      }).formatToParts(new Date());
      const get = (type) => (parts.find((p) => p.type === type) || {}).value;
      const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const today = names.indexOf(get('weekday'));
      const now = (Number(get('hour')) % 24) * 60 + Number(get('minute'));
      if (today === -1 || Number.isNaN(now)) return;

      const clock = (mins) => `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
      const isOpen = days.includes(today) && now >= open && now < close;

      if (isOpen) {
        label.textContent = `Open now · until ${clock(close)}`;
      } else {
        let next = today;
        for (let i = 0; i < 7; i += 1) {
          const candidate = (today + i) % 7;
          if (days.includes(candidate) && (i > 0 || now < open)) {
            next = candidate;
            break;
          }
        }
        const when = next === today && now < open ? 'today' : next === (today + 1) % 7 ? 'tomorrow' : names[next];
        label.textContent = `Closed · opens ${when} ${clock(open)}`;
        el.classList.add('contact-status--closed');
      }
      el.hidden = false;
    })
  );

  /**
   * Basket: Cart API helpers, the basket page and the mini-basket drawer.
   *
   * `cart.change` and `cart.update` join `cart.add`. They post to the Cart API, keep
   * the header count in sync and dispatch `recon:cart:changed`, deliberately not
   * `recon:cart:updated`: the drawer treats that one as "something new went in" and
   * opens on it, which a quantity tweak on the basket page must never trigger.
   */
  const cartRequest = async (endpoint, payload) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  };

  const dispatchChanged = (state) => {
    cart.render(state);
    document.dispatchEvent(new CustomEvent('recon:cart:changed', { detail: state }));
    return state;
  };

  /** Change one line: `{ line, quantity }` (1-based) or `{ id, quantity }`; `sections` optional. */
  cart.change = (payload) => cartRequest('/cart/change.js', payload).then(dispatchChanged);

  /** Update the note, attributes or several quantities: `{ note }` or `{ updates: {...} }`. */
  cart.update = (payload) => cartRequest('/cart/update.js', payload).then(dispatchChanged);

  /* Remembers the last category browsed so the basket's "Continue shopping" can return there. */
  const LAST_COLLECTION = 'recon:last-collection';
  try {
    if (/^\/collections\/[^/]+\/?$/.test(window.location.pathname)) {
      sessionStorage.setItem(LAST_COLLECTION, window.location.pathname);
    }
  } catch {
    /* Storage can be off; the link keeps its server-rendered fallback. */
  }

  const escapeHTML = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const itemsLabel = (count) => `${count} ${count === 1 ? 'item' : 'items'}`;

  /**
   * The basket page.
   *
   * Every quantity change and remove posts to the Cart API with `sections`, so the
   * response carries a fresh render of this section; that replaces the section root
   * and re-runs this setup on it. Without JS the same controls post the plain form.
   */
  const setupCart = (root) => {
    const sectionId = root.dataset.sectionId;

    /* Shopify's default page title for this template says "Shopping Cart"; the page says basket. */
    if (/^Your Shopping Cart\b/.test(document.title)) {
      document.title = document.title.replace(/^Your Shopping Cart/, 'Your basket');
    }

    const continueLink = root.querySelector('[data-cart-continue]');
    if (continueLink) {
      try {
        const last = sessionStorage.getItem(LAST_COLLECTION);
        if (last) continueLink.href = last;
      } catch {
        /* Keep the fallback href. */
      }
    }

    /* A live region outside the swapped markup, so it exists before the announcement. */
    let live = document.getElementById('recon-cart-live');
    if (!live) {
      live = document.createElement('p');
      live.id = 'recon-cart-live';
      live.className = 'sr-only';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.append(live);
    }
    const announce = (text) => {
      live.textContent = '';
      window.setTimeout(() => {
        live.textContent = text;
      }, 50);
    };

    const swap = (html) => {
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart]');
      if (!fresh) return null;
      root.replaceWith(fresh);
      init('[data-cart]', setupCart);
      return fresh;
    };

    let busy = false;
    const change = async (payload, { focusLine, message } = {}) => {
      if (busy) return;
      busy = true;
      root.setAttribute('aria-busy', 'true');
      root.querySelectorAll('[data-qty], [data-qty-dec], [data-qty-inc], [data-cart-checkout]').forEach((el) => {
        el.disabled = true;
      });

      try {
        const state = await cart.change({ ...payload, sections: sectionId });
        const fresh = swap(state.sections && state.sections[sectionId]);
        if (!fresh) {
          window.location.reload();
          return;
        }

        announce(`${message} ${state.item_count > 0 ? `${itemsLabel(state.item_count)} in your basket, ${formatMoney(state.total_price)} ex VAT.` : 'Your basket is now empty.'}`);

        /* The "often bought with" grid only makes sense while there is something to buy it with. */
        document.querySelectorAll('[data-cart-recommendations]').forEach((section) => {
          section.hidden = state.item_count === 0;
        });

        const target =
          (focusLine && fresh.querySelector(`[data-qty][data-line="${focusLine}"]`)) ||
          fresh.querySelector('.cart__title, .cart-empty__title');
        if (target) {
          if (!target.matches('input')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      } catch (error) {
        announce((error && error.description) || 'Sorry, your basket could not be updated.');
        root.removeAttribute('aria-busy');
        root.querySelectorAll('[data-qty], [data-qty-inc], [data-cart-checkout]').forEach((el) => {
          el.disabled = false;
        });
        root.querySelectorAll('[data-cart-line]').forEach((line) => {
          const input = line.querySelector('[data-qty]');
          const dec = line.querySelector('[data-qty-dec]');
          if (input) input.value = input.defaultValue;
          if (dec) dec.disabled = Number(input && input.defaultValue) <= 1;
        });
      } finally {
        busy = false;
      }
    };

    root.querySelectorAll('[data-cart-line]').forEach((line) => {
      const lineIndex = Number(line.dataset.cartLine);
      const input = line.querySelector('[data-qty]');
      const dec = line.querySelector('[data-qty-dec]');
      const inc = line.querySelector('[data-qty-inc]');
      const remove = line.querySelector('[data-cart-remove]');
      const titleEl = line.querySelector('.cart-line__link');
      const title = titleEl ? titleEl.textContent.trim() : 'item';

      const commit = (value) => {
        if (!input) return;
        const quantity = Math.max(0, Math.min(99, Number.isNaN(value) ? 1 : value));
        if (quantity === Number(input.defaultValue)) {
          input.value = input.defaultValue;
          return;
        }
        input.value = String(quantity);
        change(
          { line: lineIndex, quantity },
          {
            focusLine: quantity > 0 ? lineIndex : null,
            message: quantity > 0 ? `${title} quantity set to ${quantity}.` : `Removed ${title}.`,
          }
        );
      };

      if (input) {
        input.addEventListener('change', () => commit(parseInt(input.value, 10)));
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit(parseInt(input.value, 10));
        });
      }
      if (dec) dec.addEventListener('click', () => commit(parseInt(input.value, 10) - 1));
      if (inc) inc.addEventListener('click', () => commit(parseInt(input.value, 10) + 1));
      if (remove) {
        remove.addEventListener('click', (event) => {
          event.preventDefault();
          change({ line: lineIndex, quantity: 0 }, { message: `Removed ${title}.` });
        });
      }
    });

    /* The order note saves itself on blur; no need to redraw the page for it. */
    const note = root.querySelector('[data-cart-note]');
    const noteStatus = root.querySelector('[data-cart-note-status]');
    if (note) {
      let noteTimer;
      note.addEventListener('change', async () => {
        if (noteStatus) noteStatus.textContent = '';
        try {
          await cart.update({ note: note.value });
          if (noteStatus) {
            noteStatus.textContent = 'Note saved';
            window.clearTimeout(noteTimer);
            noteTimer = window.setTimeout(() => {
              noteStatus.textContent = '';
            }, 3000);
          }
        } catch {
          if (noteStatus) noteStatus.textContent = 'The note could not be saved. It will be sent with your order instead.';
        }
      });
    }
  };

  ready(() => init('[data-cart]', setupCart));

  /* Something added from the "often bought with" grid, or the free gift coming or going: redraw the basket page. */
  const refreshCartPage = async () => {
    const root = document.querySelector('[data-cart]');
    if (!root) return;
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(root.dataset.sectionId)}`);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-cart]');
      if (!fresh) return;
      root.replaceWith(fresh);
      init('[data-cart]', setupCart);
    } catch {
      /* The header count is already right; the page catches up on the next change. */
    }
  };
  document.addEventListener('recon:cart:updated', refreshCartPage);

  /** `snippets/free-gift-state.liquid`, from the page itself or a fresh section render. */
  const readFreeGift = (root) => {
    const el = root.querySelector('[data-free-gift]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };
  const freeGift = readFreeGift(document);

  /**
   * The mini-basket drawer.
   *
   * Opens when anything is added (`recon:cart:updated`) or when the header basket
   * link is clicked, and repaints its list from the Cart API JSON. The markup it
   * builds mirrors `snippets/mini-cart.liquid`, which renders the first paint.
   */
  const PACKAGE_ICON =
    '<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" /><path d="M12 22V12" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="m7.5 4.27 9 5.15" /></svg>';
  const MINUS_ICON =
    '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /></svg>';
  const PLUS_ICON =
    '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="M12 5v14" /></svg>';
  const GIFT_ICON =
    '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" /><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" /></svg>';

  ready(() =>
    init('[data-mini-cart]', (drawer) => {
      const lines = drawer.querySelector('[data-mini-cart-lines]');
      const empty = drawer.querySelector('[data-mini-cart-empty]');
      const foot = drawer.querySelector('[data-mini-cart-foot]');
      const count = drawer.querySelector('[data-mini-cart-count]');
      const subtotal = drawer.querySelector('[data-mini-cart-subtotal]');
      const status = drawer.querySelector('[data-mini-cart-status]');
      const closeButton = drawer.querySelector('[data-mini-cart-close]');
      const addedNote = drawer.querySelector('[data-mini-cart-added]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const thumb = (url) => {
        try {
          const parsed = new URL(url);
          parsed.searchParams.set('width', '160');
          return parsed.toString();
        } catch {
          return url;
        }
      };

      const lineMarkup = (item, index) => {
        const title = escapeHTML(item.product_title);
        const quantity = Number(item.quantity);
        const qtyId = `mini-cart-qty-${index + 1}`;
        return `
        <li class="mini-cart__line" data-key="${escapeHTML(item.key)}" data-quantity="${quantity}">
          <a class="mini-cart__media" href="${escapeHTML(item.url)}" tabindex="-1" aria-hidden="true">
            ${
              item.image
                ? `<img class="mini-cart__image" src="${escapeHTML(thumb(item.image))}" alt="" width="80" height="80" loading="lazy">`
                : `<span class="mini-cart__placeholder">${PACKAGE_ICON}</span>`
            }
          </a>
          <div class="mini-cart__line-body">
            <a class="mini-cart__line-title" href="${escapeHTML(item.url)}">${title}</a>
            ${item.variant_title ? `<p class="mini-cart__line-variant">${escapeHTML(item.variant_title)}</p>` : ''}
          </div>
          <p class="mini-cart__line-price">
            <span class="mini-cart__line-amount">${formatMoney(item.final_line_price)}</span>
            <span class="mini-cart__line-vat">+VAT</span>
          </p>
          <div class="mini-cart__line-controls">
            ${
              freeGift && freeGift.enabled && item.variant_id === freeGift.variant
                ? `<p class="mini-cart__gift">${GIFT_ICON} ${escapeHTML(freeGift.label)}</p>`
                : `
            <div class="quantity mini-cart__qty">
              <button type="button" class="quantity__button" aria-label="Decrease quantity of ${title}" data-mini-qty-dec${quantity <= 1 ? ' disabled' : ''}>${MINUS_ICON}</button>
              <label class="sr-only" for="${qtyId}">Quantity of ${title}</label>
              <input class="quantity__input" id="${qtyId}" type="number" value="${quantity}" min="0" max="99" step="1" inputmode="numeric" data-mini-qty>
              <button type="button" class="quantity__button" aria-label="Increase quantity of ${title}" data-mini-qty-inc>${PLUS_ICON}</button>
            </div>
            <button type="button" class="mini-cart__remove" data-mini-cart-remove aria-label="Remove ${title}">Remove</button>`
            }
          </div>
        </li>`;
      };

      /* What the drawer last showed, keyed by line, so a fresh add can be told apart from a repaint. */
      let known = new Map();
      const remember = (state) => {
        known = new Map(state.items.map((item) => [item.key, Number(item.quantity)]));
      };
      if (lines) {
        known = new Map([...lines.querySelectorAll('[data-key]')].map((li) => [li.dataset.key, Number(li.dataset.quantity)]));
      }

      let newTimer;
      const render = (state, { highlight } = {}) => {
        if (!state || !Array.isArray(state.items)) return;
        const has = state.item_count > 0;
        if (lines) {
          lines.innerHTML = state.items.map(lineMarkup).join('');
          lines.hidden = !has;
          if (highlight && highlight.size) {
            lines.querySelectorAll('[data-key]').forEach((li) => {
              if (highlight.has(li.dataset.key)) li.classList.add('is-new');
            });
            window.clearTimeout(newTimer);
            newTimer = window.setTimeout(() => {
              lines.querySelectorAll('.is-new').forEach((li) => li.classList.remove('is-new'));
            }, 2500);
          }
        }
        if (empty) empty.hidden = has;
        if (foot) foot.hidden = !has;
        if (count) count.textContent = has ? `(${itemsLabel(state.item_count)})` : '';
        if (subtotal) subtotal.textContent = formatMoney(state.total_price);
        remember(state);
      };

      /* ---------------- Open / close ---------------- */
      let opener = null;
      let closing = false;

      const open = (trigger, { added = false } = {}) => {
        if (addedNote) addedNote.hidden = !added;
        if (drawer.open) return;
        opener = trigger || document.activeElement;
        drawer.classList.remove('is-closing');
        drawer.showModal();
        if (closeButton) closeButton.focus();
      };

      const close = () => {
        if (!drawer.open || closing) return;
        const finish = () => {
          closing = false;
          drawer.classList.remove('is-closing');
          drawer.close();
        };
        if (reducedMotion.matches) {
          finish();
          return;
        }
        closing = true;
        drawer.classList.add('is-closing');
        const onEnd = (event) => {
          if (event.target !== drawer) return;
          drawer.removeEventListener('transitionend', onEnd);
          finish();
        };
        drawer.addEventListener('transitionend', onEnd);
        window.setTimeout(() => {
          if (!closing) return;
          drawer.removeEventListener('transitionend', onEnd);
          finish();
        }, 400);
      };

      drawer.addEventListener('close', () => {
        if (addedNote) addedNote.hidden = true;
        if (opener && opener.isConnected && typeof opener.focus === 'function') {
          opener.focus({ preventScroll: true });
        }
        opener = null;
      });

      /* Escape: run the same slide-out instead of the browser's instant close. */
      drawer.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      });

      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) close();
      });

      if (closeButton) closeButton.addEventListener('click', close);

      /* A modal dialog already keeps the page inert; this just wraps Tab at the ends. */
      drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])')].filter(
          (el) => !el.hidden && !el.closest('[hidden]') && el.getClientRects().length > 0
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      /* Header basket link: the drawer with JS, the page without. Never on the basket page itself. */
      document.querySelectorAll('.header__basket, [data-mini-cart-open]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (document.querySelector('[data-cart]')) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          open(link);
        });
      });

      /* ---------------- Cart events ---------------- */
      document.addEventListener('recon:cart:updated', (event) => {
        const state = event.detail;
        const items = state && Array.isArray(state.items) ? state.items : [];
        const fresh = new Set(items.filter((item) => Number(item.quantity) > (known.get(item.key) || 0)).map((item) => item.key));
        render(state, { highlight: fresh });
        if (document.querySelector('[data-cart]')) return;
        if (status) status.textContent = 'Added to your basket.';
        open(null, { added: true });
      });

      document.addEventListener('recon:cart:changed', (event) => render(event.detail));

      /* Back/forward cache restores the old drawer markup; refresh it from the API. */
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) cart.fetch().then(render).catch(() => {});
      });

      /* ---------------- Quantity + remove inside the drawer ---------------- */
      if (lines) {
        let pending = false;

        /* Lines are addressed by key, not index, so a stale drawer can never change the wrong line. */
        const change = async (li, quantity, { refocus } = {}) => {
          if (pending || !li) return;
          const key = li.dataset.key;
          const titleEl = li.querySelector('.mini-cart__line-title');
          const title = titleEl ? titleEl.textContent.trim() : 'item';
          pending = true;
          drawer.setAttribute('aria-busy', 'true');
          li.querySelectorAll('button, input').forEach((el) => {
            el.disabled = true;
          });
          try {
            const state = await cart.change({ id: key, quantity });
            if (status) {
              status.textContent = `${quantity > 0 ? `${title} quantity set to ${quantity}.` : `Removed ${title}.`} ${
                state.item_count > 0 ? `${itemsLabel(state.item_count)} in your basket.` : 'Your basket is now empty.'
              }`;
            }
            /* The list was rebuilt, so put focus back on the same control of the same line. */
            const line = lines.querySelector(`[data-key="${CSS.escape(key)}"]`);
            const again = line && refocus && line.querySelector(refocus);
            const target = (again && !again.disabled && again) || (line && line.querySelector('[data-mini-qty]')) || closeButton;
            if (target) target.focus();
          } catch (error) {
            if (status) status.textContent = (error && error.description) || 'Sorry, your basket could not be updated.';
            const input = li.querySelector('[data-mini-qty]');
            if (input) input.value = li.dataset.quantity;
            li.querySelectorAll('button, input').forEach((el) => {
              el.disabled = false;
            });
            const dec = li.querySelector('[data-mini-qty-dec]');
            if (dec) dec.disabled = Number(li.dataset.quantity) <= 1;
          } finally {
            drawer.removeAttribute('aria-busy');
            pending = false;
          }
        };

        const commit = (li, value, refocus) => {
          if (!li) return;
          const current = Number(li.dataset.quantity);
          const quantity = Math.max(0, Math.min(99, Number.isNaN(value) ? current : value));
          const input = li.querySelector('[data-mini-qty]');
          if (quantity === current) {
            if (input) input.value = String(current);
            return;
          }
          if (input) input.value = String(quantity);
          change(li, quantity, { refocus: quantity > 0 ? refocus : null });
        };

        lines.addEventListener('click', (event) => {
          const li = event.target.closest('.mini-cart__line');
          if (!li) return;
          const input = li.querySelector('[data-mini-qty]');
          const current = input ? parseInt(input.value, 10) : Number(li.dataset.quantity);
          if (event.target.closest('[data-mini-cart-remove]')) change(li, 0);
          else if (event.target.closest('[data-mini-qty-dec]')) commit(li, current - 1, '[data-mini-qty-dec]');
          else if (event.target.closest('[data-mini-qty-inc]')) commit(li, current + 1, '[data-mini-qty-inc]');
        });

        lines.addEventListener('change', (event) => {
          const input = event.target.closest('[data-mini-qty]');
          if (input) commit(input.closest('.mini-cart__line'), parseInt(input.value, 10), '[data-mini-qty]');
        });

        lines.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || !event.target.matches('[data-mini-qty]')) return;
          event.preventDefault();
          commit(event.target.closest('.mini-cart__line'), parseInt(event.target.value, 10), '[data-mini-qty]');
        });
      }
    })
  );

  /**
   * The automatic free gift (Theme settings › Free gift).
   *
   * After every cart change this re-renders `sections/free-gift-state.liquid`, since the
   * Cart API JSON cannot say which collections a product is in, and sets the gift to one
   * while the basket qualifies and to none when it does not. The price is Shopify's job:
   * an automatic Buy X get Y discount makes the gift free. If the gift ever comes back
   * charged, that discount is off or set up wrongly, so the gift is taken out again and
   * the script stands down for the rest of the visit rather than sell it by accident.
   */
  if (freeGift && freeGift.enabled) {
    const GIFT_OFF = 'recon:free-gift-off';
    const standingDown = () => {
      try {
        return sessionStorage.getItem(GIFT_OFF) === '1';
      } catch {
        return false;
      }
    };

    const settle = async (state) => {
      if (!state || !state.enabled || standingDown()) return;
      const target = state.qualifies ? 1 : 0;
      if (state.quantity === target) return;

      const after = await cart.update({ updates: { [state.variant]: target } });
      const line = after.items.find((item) => item.variant_id === state.variant);
      if (line && line.final_line_price > 0) {
        try {
          sessionStorage.setItem(GIFT_OFF, '1');
        } catch {
          /* Without storage it may be tried again on the next change, and removed again. */
        }
        await cart.update({ updates: { [state.variant]: 0 } });
      }
      await refreshCartPage();
    };

    /* One check at a time; a change that lands mid-check (including the gift's own) queues one more. */
    let running = false;
    let queued = false;
    const check = async (state) => {
      if (running) {
        queued = true;
        return;
      }
      running = true;
      try {
        let next = state;
        do {
          queued = false;
          if (!next) {
            const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}?section_id=free-gift-state`);
            next = response.ok ? readFreeGift(new DOMParser().parseFromString(await response.text(), 'text/html')) : null;
          }
          await settle(next);
          next = null;
        } while (queued);
      } catch {
        /* Checkout still prices it correctly; the next cart change tries again. */
      } finally {
        running = false;
      }
    };

    document.addEventListener('recon:cart:updated', () => check());
    document.addEventListener('recon:cart:changed', () => check());
    ready(() => check(freeGift));
  }

  /**
   * Predictive search in the header.
   *
   * The search form is a plain GET form that works on its own. This turns its input
   * into a combobox: after 250ms of quiet typing (two characters or more) it fetches
   * /search/suggest.json and fills the listbox under the input with products,
   * collections, pages and articles, plus a "see all results" row. Arrow keys move
   * the highlight, Enter opens the highlighted row, Escape and clicks outside close it.
   * Enter with nothing highlighted still submits the form.
   */
  ready(() =>
    init('[data-predictive]', (form) => {
      const input = form.querySelector('[data-predictive-input]');
      const list = form.querySelector('[data-predictive-list]');
      const status = form.querySelector('[data-predictive-status]');
      if (!input || !list) return;

      const MIN_CHARS = 2;
      const DELAY = 250;
      /* Shopify's limit is per type only with limit_scope=each; the smaller
         groups are trimmed client-side so the list stays product-led. */
      const LIMIT = 6;
      const SHOW = { products: 6, collections: 3, content: 2 };

      /* The full placeholder does not fit the phone-width box, so a shorter one takes over there. */
      const shortPlaceholder = input.dataset.placeholderShort;
      if (shortPlaceholder) {
        const fullPlaceholder = input.placeholder;
        const narrow = window.matchMedia('(max-width: 63.99em)');
        const applyPlaceholder = () => {
          input.placeholder = narrow.matches ? shortPlaceholder : fullPlaceholder;
        };
        applyPlaceholder();
        narrow.addEventListener('change', applyPlaceholder);
      }
      const cache = new Map();
      const searchPath = new URL(form.action, window.location.href).pathname;

      let controller = null;
      let timer = null;
      let options = [];
      let active = -1;
      let lastQuery = '';

      const ICONS = {
        package:
          '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 8.7 5 8.7-5"/></svg>',
        layers:
          '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/></svg>',
        file:
          '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>',
        arrow:
          '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>',
      };

      const escapeHTML = (value) =>
        String(value ?? '').replace(
          /[&<>"']/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
        );

      /* Bold the typed term inside a title. Both sides are escaped first so the
         pattern matches the markup it searches. */
      const highlight = (text, query) => {
        const safe = escapeHTML(text);
        const term = escapeHTML(query.trim());
        if (!term) return safe;
        const pattern = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
        return safe.replace(pattern, '<mark>$1</mark>');
      };

      const thumb = (url, fallback) => {
        if (!url) return `<span class="predictive__thumb">${ICONS[fallback]}</span>`;
        const src = `${url}${url.includes('?') ? '&' : '?'}width=88`;
        return `<span class="predictive__thumb"><img src="${escapeHTML(src)}" alt="" width="44" height="44" loading="lazy"></span>`;
      };

      const price = (product) => {
        const min = Math.round(Number(product.price_min || product.price || 0) * 100);
        const max = Math.round(Number(product.price_max || product.price || 0) * 100);
        const from = max > min ? 'From ' : '';
        return `<span class="predictive__price">${from}${formatMoney(min)}<span class="predictive__vat">+VAT</span></span>`;
      };

      const searchUrl = (query) => `${searchPath}?q=${encodeURIComponent(query)}`;

      const setActive = (index) => {
        active = index;
        options.forEach((option, i) => {
          option.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        if (index >= 0 && options[index]) {
          input.setAttribute('aria-activedescendant', options[index].id);
          options[index].scrollIntoView({ block: 'nearest' });
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      };

      const open = () => {
        if (!list.innerHTML) return;
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        setActive(-1);
      };

      const render = (query, results) => {
        let count = 0;
        const option = (href, inner, extraClass = '') =>
          `<a class="predictive__option${extraClass ? ` ${extraClass}` : ''}" role="option" id="${list.id}-option-${count++}" aria-selected="false" href="${escapeHTML(href)}">${inner}</a>`;
        const group = (label, rows) =>
          rows.length
            ? `<div class="predictive__group" role="group" aria-label="${label}"><p class="predictive__label" role="presentation">${label}</p>${rows.join('')}</div>`
            : '';

        const products = (results.products || []).slice(0, SHOW.products);
        const collections = (results.collections || []).slice(0, SHOW.collections);
        const content = [...(results.pages || []), ...(results.articles || [])].slice(0, SHOW.content);

        const productGroup = group(
          'Products',
            products.map((product) => {
              const meta = [product.vendor, product.available === false ? 'Out of stock' : '']
                .filter(Boolean)
                .join(' · ');
              return option(
                product.url,
                `${thumb(product.image || (product.featured_image && product.featured_image.url), 'package')}<span class="predictive__body"><span class="predictive__title">${highlight(product.title, query)}</span>${meta ? `<span class="predictive__meta">${escapeHTML(meta)}</span>` : ''}</span>${price(product)}`
              );
            })
          );
        const asideGroups = [
          group(
            'Categories',
            collections.map((collection) =>
              option(
                collection.url,
                `${thumb(collection.featured_image && collection.featured_image.url, 'layers')}<span class="predictive__body"><span class="predictive__title">${highlight(collection.title, query)}</span></span>`
              )
            )
          ),
          group(
            'Pages and articles',
            content.map((item) =>
              option(
                item.url,
                `${thumb(item.image, 'file')}<span class="predictive__body"><span class="predictive__title">${highlight(item.title, query)}</span></span>`
              )
            )
          ),
        ].join('');
        /* Products on the left, everything else beside them (the stylesheet
           splits the two only from desktop; below that they stack). */
        const aside = asideGroups ? `<div class="predictive__aside" role="presentation">${asideGroups}</div>` : '';
        const split = productGroup && aside ? ' predictive__groups--split' : '';
        const markup =
          productGroup || aside
            ? `<div class="predictive__groups${split}" role="presentation">${productGroup}${aside}</div>`
            : '';

        const total = products.length + collections.length + content.length;
        const empty = total
          ? ''
          : `<p class="predictive__empty" role="presentation">Nothing matched &ldquo;${escapeHTML(query)}&rdquo; yet. Press Enter to search every product, page and article, or ring us for anything not listed.</p>`;

        list.innerHTML =
          markup +
          empty +
          option(
            searchUrl(query),
            `<span>See all results for &ldquo;${escapeHTML(query)}&rdquo;</span>${ICONS.arrow}`,
            'predictive__all'
          );

        options = [...list.querySelectorAll('[role="option"]')];
        setActive(-1);
        if (status) {
          status.textContent = total
            ? `${total} suggestion${total === 1 ? '' : 's'}. Use the up and down arrows to review them and Enter to open one.`
            : 'No suggestions. Press Enter to search everything.';
        }
      };

      const fetchSuggestions = async (query) => {
        if (cache.has(query)) return cache.get(query);
        if (controller) controller.abort();
        controller = new AbortController();

        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product,collection,page,article',
          'resources[limit]': String(LIMIT),
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
        });
        const response = await fetch(`${searchPath}/suggest.json?${params}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Suggest failed: ${response.status}`);

        const data = await response.json();
        const results = (data.resources && data.resources.results) || {};
        cache.set(query, results);
        return results;
      };

      const update = async () => {
        const query = input.value.trim();
        if (query.length < MIN_CHARS) {
          lastQuery = '';
          list.innerHTML = '';
          close();
          return;
        }
        if (query === lastQuery) {
          open();
          return;
        }
        lastQuery = query;
        form.setAttribute('aria-busy', 'true');

        try {
          const results = await fetchSuggestions(query);
          /* The user may have kept typing while this request was in flight. */
          if (input.value.trim() !== query) return;
          render(query, results);
          open();
        } catch (error) {
          if (error.name !== 'AbortError') close();
        } finally {
          form.removeAttribute('aria-busy');
        }
      };

      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(update, DELAY);
      });

      /* The native clear button on a search input fires `search`, not `input`. */
      input.addEventListener('search', () => {
        clearTimeout(timer);
        update();
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= MIN_CHARS) update();
      });

      input.addEventListener('keydown', (event) => {
        const isOpen = !list.hidden && options.length > 0;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!isOpen) {
              update();
              return;
            }
            setActive((active + 1) % options.length);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (!isOpen) return;
            setActive(active <= 0 ? options.length - 1 : active - 1);
            break;
          case 'Escape':
            if (!list.hidden) {
              event.preventDefault();
              close();
            }
            break;
          case 'Enter':
            if (isOpen && active >= 0) {
              event.preventDefault();
              window.location.assign(options[active].href);
            }
            break;
          case 'Tab':
            close();
            break;
          default:
        }
      });

      list.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) {
          const index = options.indexOf(option);
          if (index !== active) setActive(index);
        }
      });

      /* Mousedown rather than click: the input's blur must not close the list
         before a click on a suggestion lands. */
      document.addEventListener('mousedown', (event) => {
        if (!form.contains(event.target)) close();
      });

      document.addEventListener('focusin', (event) => {
        if (!form.contains(event.target)) close();
      });
    })
  );

  /**
   * Article share: copy the page link. The button swaps its label for a moment
   * and reports through its own live region, so screen readers hear it too.
   */
  ready(() =>
    init("[data-copy-link]", (button) => {
      const label = button.querySelector("[data-copy-label]") || button;
      const original = label.textContent;
      const status = document.createElement("span");
      status.className = "sr-only";
      status.setAttribute("aria-live", "polite");
      button.insertAdjacentElement("afterend", status);
      let timer;

      button.addEventListener("click", async () => {
        const url = button.dataset.copyLink || window.location.href;
        try {
          await navigator.clipboard.writeText(url);
          label.textContent = "Link copied";
          status.textContent = "Link copied to clipboard";
          button.classList.add("is-copied");
        } catch {
          label.textContent = "Copy failed";
          status.textContent = "Could not copy the link";
        }
        clearTimeout(timer);
        timer = setTimeout(() => {
          label.textContent = original;
          button.classList.remove("is-copied");
        }, 2200);
      });
    })
  );

  /**
   * 404 search: swaps to the same phone-width placeholder as the header search,
   * on the same breakpoint, so the two boxes read as one search brought forward.
   * Re-run whenever the theme editor re-renders a section, since the replacement
   * input arrives with the long placeholder.
   */
  const setupPlaceholderSwap = (input) => {
    const shortPlaceholder = input.dataset.placeholderShort;
    if (!shortPlaceholder) return;
    const fullPlaceholder = input.placeholder;
    const narrow = window.matchMedia('(max-width: 63.99em)');
    const applyPlaceholder = () => {
      input.placeholder = narrow.matches ? shortPlaceholder : fullPlaceholder;
    };
    applyPlaceholder();
    narrow.addEventListener('change', applyPlaceholder);
  };

  ready(() => init('[data-placeholder-swap]', setupPlaceholderSwap));
  document.addEventListener('shopify:section:load', () =>
    init('[data-placeholder-swap]', setupPlaceholderSwap)
  );

  /**
   * Promotional pop-up (`sections/promo-popup.liquid`). Opens once on whichever
   * armed trigger fires first: a timer, a scroll depth, or exit intent (desktop
   * pointers only). Once shown it stays away for `data-days` days, or for the
   * rest of the visit when that is 0. In the theme editor it never opens by
   * itself: selecting the section opens it, deselecting closes it.
   */
  const setupPromoPopup = (dialog) => {
    const key = `recon:promo:${dialog.dataset.key}`;
    const days = Number(dialog.dataset.days) || 0;
    const delay = Number(dialog.dataset.delay) || 0;
    const scrollDepth = Number(dialog.dataset.scroll) || 0;
    const exitIntent = dialog.dataset.exit === 'true';
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const copyButton = dialog.querySelector('[data-promo-copy]');
    const copyLabel = dialog.querySelector('[data-promo-copy-label]');
    const copyStatus = dialog.querySelector('[data-promo-copy-status]');

    /* Storage can throw (private modes, blocked cookies): treat that as never seen. */
    const seen = () => {
      try {
        if (sessionStorage.getItem(key)) return true;
        const shownAt = Number(localStorage.getItem(key));
        return days > 0 && shownAt > 0 && Date.now() - shownAt < days * 86400000;
      } catch {
        return false;
      }
    };

    const remember = () => {
      try {
        sessionStorage.setItem(key, '1');
        localStorage.setItem(key, String(Date.now()));
      } catch {
        /* Nothing to do: it simply may show again. */
      }
    };

    let closing = false;

    const open = () => {
      if (dialog.open || !dialog.isConnected) return;
      dialog.classList.remove('is-closing');
      dialog.showModal();
    };

    const close = () => {
      if (!dialog.open || closing) return;
      const finish = () => {
        closing = false;
        dialog.classList.remove('is-closing');
        dialog.close();
      };
      if (reducedMotion.matches) {
        finish();
        return;
      }
      closing = true;
      dialog.classList.add('is-closing');
      window.setTimeout(finish, 300);
    };

    /* Escape and backdrop clicks run the same fade as the close button. */
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.querySelectorAll('[data-promo-close]').forEach((button) => button.addEventListener('click', close));

    if (copyButton) {
      let resetTimer;
      copyButton.addEventListener('click', async () => {
        const code = copyButton.dataset.promoCopy;
        try {
          await navigator.clipboard.writeText(code);
        } catch {
          /* No clipboard access: select the code so it can be copied by hand. */
          const value = dialog.querySelector('.promo-popup__code-value');
          if (value) window.getSelection().selectAllChildren(value);
          return;
        }
        copyButton.classList.add('is-copied');
        if (copyLabel) copyLabel.textContent = 'Copied';
        if (copyStatus) copyStatus.textContent = `Discount code ${code} copied`;
        window.clearTimeout(resetTimer);
        resetTimer = window.setTimeout(() => {
          copyButton.classList.remove('is-copied');
          if (copyLabel) copyLabel.textContent = 'Copy code';
          if (copyStatus) copyStatus.textContent = '';
        }, 2500);
      });
    }

    if (designMode) {
      const sectionId = dialog.dataset.sectionId;
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === sectionId) open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === sectionId) close();
      });
      return;
    }

    if (seen()) return;

    const cleanups = [];
    const disarm = () => cleanups.splice(0).forEach((fn) => fn());

    /* Another dialog (the basket, the filter drawer) takes priority: leave the other triggers armed. */
    const trigger = () => {
      if (document.querySelector('dialog[open]')) return;
      disarm();
      remember();
      open();
    };

    if (delay > 0) {
      const timer = window.setTimeout(trigger, delay * 1000);
      cleanups.push(() => window.clearTimeout(timer));
    }

    if (scrollDepth > 0) {
      const onScroll = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable > 0 && window.scrollY / scrollable >= scrollDepth / 100) trigger();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));
    }

    /* The pointer leaving through the top edge, towards the tabs and address bar. Armed after a
       short grace period so a pointer already heading for the toolbar on arrival does not count. */
    if (exitIntent && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const onLeave = (event) => {
        if (!event.relatedTarget && event.clientY <= 0) trigger();
      };
      const armTimer = window.setTimeout(() => document.addEventListener('mouseout', onLeave), 3000);
      cleanups.push(() => {
        window.clearTimeout(armTimer);
        document.removeEventListener('mouseout', onLeave);
      });
    }
  };

  ready(() => init('[data-promo-popup]', setupPromoPopup));
  document.addEventListener('shopify:section:load', () => init('[data-promo-popup]', setupPromoPopup));
  /**
   * Policy pages: Shopify renders the policy body itself, so the aside index is
   * built here from the h2s. Each heading gets a stable id, the list fills the
   * card in `layout/theme.liquid`, and the marker follows the heading in view.
   * The opening "Last updated" line is tagged as a caption only when it really
   * is that line.
   */
  ready(() =>
    init('[data-policy-toc]', (nav) => {
      const list = nav.querySelector('[data-policy-toc-list]');
      const body = document.querySelector('.shopify-policy__body .rte');
      if (!list || !body) return;

      const first = body.querySelector('p');
      const firstText = first ? first.textContent.trim() : '';
      if (first && /^last updated/i.test(firstText) && firstText.length < 80) {
        first.classList.add('rte__caption');
      }

      const headings = Array.from(body.querySelectorAll('h2')).filter((heading) => heading.textContent.trim());
      if (headings.length < 2) return;

      const links = headings.map((heading) => {
        if (!heading.id) {
          const base =
            heading.textContent
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '') || 'section';
          let id = base;
          let n = 2;
          while (document.getElementById(id)) id = `${base}-${n++}`;
          heading.id = id;
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'page-toc__link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
        return link;
      });
      const count = nav.querySelector('[data-policy-toc-count]');
      if (count) count.textContent = `${headings.length} sections`;
      nav.hidden = false;

      /* Below 64em the index is a closed disclosure above the text; from 64em it is always open and its summary leaves the tab order, since it no longer does anything. */
      const details = nav.querySelector('[data-policy-toc-details]');
      const summary = details ? details.querySelector('summary') : null;
      const wide = window.matchMedia('(min-width: 64em)');
      const syncOpen = () => {
        if (details) details.open = wide.matches;
        if (summary) summary.tabIndex = wide.matches ? -1 : 0;
      };
      syncOpen();
      wide.addEventListener('change', syncOpen);
      details?.addEventListener('toggle', () => {
        if (wide.matches && !details.open) details.open = true;
      });

      /* The list scrolls internally when it is taller than the column, so keep the marker in view. */
      const box = list;
      let current = -1;
      const setCurrent = (index) => {
        if (index === current) return;
        if (current >= 0) links[current].removeAttribute('aria-current');
        current = index;
        if (current < 0) return;
        links[current].setAttribute('aria-current', 'true');
        if (box && box.scrollHeight > box.clientHeight) {
          box.scrollTo({ top: links[current].offsetTop - box.clientHeight / 2, behavior: 'auto' });
        }
      };

      /* The current section is the last heading that has passed the top third of the viewport. */
      const update = () => {
        const line = window.innerHeight / 3;
        let index = -1;
        headings.forEach((heading, i) => {
          if (heading.getBoundingClientRect().top <= line) index = i;
        });
        setCurrent(index);
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    })
  );

  /**
   * Trade application form.
   *
   * The customer form only stores the email, name, phone and tags on the customer record,
   * so on submit the company fields are written into the note ahead of the message. Without
   * JavaScript the fields still post; the note is then just the message.
   */
  ready(() =>
    // Not `init`: the contact-form block above already marks this form ready, so guard separately.
    document.querySelectorAll("[data-trade-application]").forEach((form) => {
      const note = form.querySelector("[data-trade-note]");
      if (!note || form.dataset.tradeReady) return;
      form.dataset.tradeReady = "1";
      const original = { value: null };
      form.addEventListener("submit", () => {
        if (typeof form.checkValidity === "function" && !form.checkValidity()) return;
        if (original.value === null) original.value = note.value;
        const lines = [...form.querySelectorAll("[data-trade-field]")]
          .filter((field) => field.value.trim())
          .map((field) => field.dataset.tradeField + ": " + field.value.trim());
        const message = original.value.trim();
        note.value = lines.concat(message ? ["", message] : []).join("\n");
      });
      // A blocked or bounced submit (captcha, back button) must not leave the merged text in the box.
      window.addEventListener("pageshow", () => {
        if (original.value !== null) note.value = original.value;
      });
    })
  );

  /**
   * Featured product gallery: clicking a thumbnail swaps the main image in place.
   * Without JavaScript the first image and the row of thumbnails still render.
   */
  ready(() =>
    init('[data-featured-gallery]', (gallery) => {
      const main = gallery.querySelector('[data-featured-main]');
      const thumbs = [...gallery.querySelectorAll('[data-featured-thumb]')];
      if (!main || !thumbs.length) return;

      thumbs.forEach((thumb, index) =>
        thumb.addEventListener('click', () => {
          main.src = thumb.dataset.src;
          main.srcset = thumb.dataset.srcset;
          main.alt = thumb.dataset.alt;
          thumbs.forEach((t, i) => {
            if (i === index) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
        })
      );
    })
  );

  /**
   * Meet the team (`sections/team.liquid`). Below 48em the cards scroll sideways a page of
   * four at a time; the track snaps natively, and the arrows and dots move it and follow it.
   * From 48em up the controls are hidden by CSS and this does nothing visible.
   */
  const setupTeamCarousel = (carousel) => {
    const track = carousel.querySelector('[data-team-track]');
    const pages = [...carousel.querySelectorAll('[data-team-page]')];
    const dots = [...carousel.querySelectorAll('[data-team-dot]')];
    const prev = carousel.querySelector('[data-team-prev]');
    const next = carousel.querySelector('[data-team-next]');
    if (!track || pages.length < 2 || !prev || !next) return;

    const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));

    const goTo = (index) => {
      const target = pages[Math.max(0, Math.min(index, pages.length - 1))];
      track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    };

    const update = () => {
      const index = current();
      dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      prev.disabled = index <= 0;
      next.disabled = index >= pages.length - 1;
    };

    prev.addEventListener('click', () => goTo(current() - 1));
    next.addEventListener('click', () => goTo(current() + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

    let frame;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    }, { passive: true });
    update();
  };

  ready(() => init('[data-team-carousel]', setupTeamCarousel));
  document.addEventListener('shopify:section:load', () => init('[data-team-carousel]', setupTeamCarousel));
})();
